
import * as _ from "lodash";

import { ParameterEditorValue } from "../model/ParameterEditorValue";
import { IParameterSchema, ParameterType } from "./IProcessData";

import { printWarning } from "../../shared/utilities";

export function getParameterType(type: string | undefined): ParameterType {
    switch (type) {
        case "ArrayParameter": return ParameterType.ArrayParameter;
        case "BooleanParameter": return ParameterType.BooleanParameter;
        case "CancellationToken": return ParameterType.CancellationToken;
        case "ComplexParameter": return ParameterType.ComplexParameter;
        case "EnumChoiceParameter": return ParameterType.EnumChoiceParameter;
        case "EnumParameter": return ParameterType.EnumParameter;
        case "TextParameter": return ParameterType.TextParameter;
    }
    printWarning(`Unknown parameter type ${type}. Will default to TextParameter`);
    return ParameterType.TextParameter;
}

export function initializeDefaultParameterStructure(schema: IParameterSchema | undefined | null, existingStructure?: ParameterStructure | ParameterStructure[] | ParameterEditorValue | null): ParameterStructure | ParameterStructure[] | ParameterEditorValue | null {
    const parsedType = getParameterType(schema?.parameterType);
    switch (parsedType) {
        case ParameterType.ArrayParameter: {
            if (existingStructure) {
                const existingArray = existingStructure as ParameterStructure[];
                if (Array.isArray(existingArray)) {
                    return existingArray.map((item) => initializeDefaultParameterStructure(schema?.parameterTemplate, item)) as ParameterStructure[];
                } else {
                    // not an array, probably an expression, so just return as is - we don't want to fudge existing params
                    return existingStructure;
                }
            }

            if (schema?.defaultDisplayType != null && schema.defaultDisplayType.toLowerCase() === "expression") {
                return getValueForExpressionDefaultValue();
            }

            return []; // items for empty arrays will be initialized in the AddArrayItem action
        }
        case ParameterType.ComplexParameter: {
            const complexParameter: { [key: string]: any } = {};
            if (schema?.subParameters !== undefined && schema.subParameters !== null) {
                for (const subParameter of schema.subParameters) {
                    complexParameter[subParameter.name] = initializeDefaultParameterStructure(subParameter, existingStructure ? (existingStructure as { [key: string]: any })[subParameter.name] : null);
                }
            }
            return complexParameter;
        }
        case ParameterType.CancellationToken: {
            return null; // just return null for this
        }
        case ParameterType.BooleanParameter: {
            if (existingStructure) {
                return existingStructure;
            }
            if (schema?.defaultDisplayType && schema.defaultDisplayType.toLowerCase() === "expression") {
                return getValueForExpressionDefaultValue();
            } else {
                return { mode: "toggle", value: getValueForBooleanDefaultValue(schema?.defaultValue) };
            }
        }
        case ParameterType.EnumParameter: {
            return existingStructure ? existingStructure : { mode: "select", value: schema?.defaultValue ? schema.defaultValue : schema?.subParameters !== undefined && schema.subParameters !== null && schema.subParameters[0].name };
        }
        case ParameterType.TextParameter: {
            if (existingStructure) {
                return existingStructure;
            }
            let defaultValue: ParameterEditorValue;
            if (schema?.inputType === "number") {
                // numbers need to have special handling of default values,
                // because the value needs to be passed as number correctly
                if (schema.defaultDisplayType && schema.defaultDisplayType.toLowerCase() === "expression") {
                    defaultValue = defaultValue = getValueForExpressionDefaultValue();
                } else {
                    const intValue = parseInt(schema.defaultValue, 10);
                    defaultValue = { mode: "integer", value: isNaN(intValue) ? 0 : intValue };
                }
            } else if (schema?.defaultDisplayType) {
                // If there's a default display type set, use it
                // - but only if it is a non-null string!
                const defaultMode = schema.defaultDisplayType.toLowerCase();
                const value = (schema.defaultValue != null && typeof schema.defaultValue === "string")
                    ? schema.defaultValue
                    : "";

                switch (defaultMode) {
                    case "json":
                        {
                            defaultValue = { mode: "json", value: value };
                        }
                        break;
                    case "sql":
                        {
                            defaultValue = { mode: "sql", value: value };
                        }
                        break;
                    case "xml":
                        {
                            defaultValue = { mode: "xml", value: value };
                        }
                        break;
                    case "expression":
                        {
                            defaultValue = { mode: "csharp", value: value };
                        }
                        break;
                    case "text":
                    case "multilinetext":
                    default:
                        {
                            defaultValue = { mode: "text", value: trimSurroundingQuotes(value) };
                        }
                }
            } else {
                if (schema?.inputType === "expression") {
                    defaultValue = getValueForExpressionDefaultValue();
                } else if (!schema?.defaultValue) {
                    defaultValue = { mode: "text", value: "" };
                } else if (schema.defaultValue.startsWith('"') && schema.defaultValue.endsWith('"')) {
                    defaultValue = { mode: "text", value: _.trim(schema.defaultValue, '"') };
                } else {
                    // If the default value does not start with \" then it's an expression:
                    defaultValue = { mode: "csharp", value: schema.defaultValue };
                }
            }
            return defaultValue;
        }
    }
    if (existingStructure) {
        printWarning(`Unknown parameter type ${schema?.parameterType}. Will default to existing structure or empty`);
        return existingStructure;
    }
    printWarning(`Unknown parameter type ${schema?.parameterType} and no existing structure. Will default to empty`);
    return {};

    function getValueForExpressionDefaultValue(): any {
        return { mode: "csharp", value: schema?.defaultValue ? schema.defaultValue : "" };
    }
}

function getValueForBooleanDefaultValue(value: any) {
    return value != null ? value === true || value === "true" : false;
}

function trimSurroundingQuotes(value: string) {
    return (value.startsWith("\"") && value.endsWith("\"")) ? _.trim(value, '"') : value;
}

export function getInitialMode(parameterType: string, inputType: string): string {
    const parsedType = getParameterType(parameterType);
    switch (parsedType) {
        case ParameterType.BooleanParameter: {
            return "toggle";
        }
        case ParameterType.EnumParameter: {
            return "select";
        }
        case ParameterType.TextParameter: {
            if (inputType === "number") {
                return "integer";
            }
            return "text";
        }
        case ParameterType.ArrayParameter: {
            return "array";
        }
    }
    printWarning(`Unknown parameter type ${parameterType}. Will default to TextParameter`);
    return "text";
}

export interface ParameterStructure {
    [key: string]: ParameterStructure | ParameterEditorValue;
}
